import React from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Gift, Loader2, ArrowRight, Mail, Printer, Calendar as CalIcon, MessageSquare, Clock } from "lucide-react";
import api from "../lib/api";
import Grain from "../components/Grain";
import { SiteSettingsContext } from "../components/SiteFeatures";
import { usePublicData } from "../lib/publicData";
import { OfferDetails, offerPrice, durationLabel } from '../components/OfferDetails';

const INPUT_CLS = "w-full bg-white/[0.03] border border-white/10 rounded px-3 py-2.5 text-sm text-text-cream focus:border-accent-deep outline-none placeholder:text-text-cream/40";

export default function Offrir({ giftLanding = false }) {
  const initial = usePublicData();
  const navigate = useNavigate();
  const { serviceSlug } = useParams();
  const [search] = useSearchParams();
  const settings = React.useContext(SiteSettingsContext);
  const [freeAmount,setFreeAmount] = React.useState(100);
  const cancelled = search.get("cancelled") === "1";

  const [services, setServices] = React.useState((initial.services || []).filter(s=>!s.requires_quote && (!giftLanding || ['mini-noel-2026','portrait-essentiel','grossesse-classique','nouveau-ne'].includes(s.slug))));
  const [service, setService] = React.useState(null);
  const [loading, setLoading] = React.useState(!initial.services);
  const [submitting, setSubmitting] = React.useState(false);
  const [err, setErr] = React.useState("");

  const [buyer, setBuyer] = React.useState({ prenom: "", nom: "", email: "", tel: "" });
  const [recipient, setRecipient] = React.useState({ prenom: "", nom: "", email: "" });
  const [message, setMessage] = React.useState("");
  const [deliveryMode, setDeliveryMode] = React.useState(giftLanding ? "print" : "immediate"); // immediate | scheduled | print
  const [deliverAt, setDeliverAt] = React.useState("");

  React.useEffect(() => {
    api.get("/services")
      .then(r => {
        const list = r.data.filter(s => !s.requires_quote && (!giftLanding || ['mini-noel-2026','portrait-essentiel','grossesse-classique','nouveau-ne'].includes(s.slug)));
        setServices(list);
        if (giftLanding && search.get('seance')) setService(list.find(s => s.slug === search.get('seance')) || null);
        if (serviceSlug) {
          const found = list.find(s => s.slug === serviceSlug);
          if (found) setService(found);
        }
      })
      .catch(() => setErr('Les offres ne peuvent pas être chargées pour le moment.'))
      .finally(() => setLoading(false));
  }, [serviceSlug, giftLanding, search]);

  const canSubmit =
    service &&
    (service.slug !== 'montant-libre' || (Number.isInteger(freeAmount) && freeAmount >= Math.max(100, settings.gift_minimum_chf || 100) && freeAmount <= 10000)) &&
    (!giftLanding || recipient.prenom.trim()) &&
    buyer.prenom.trim() &&
    /^[^@]+@[^@]+\.[^@]+$/.test(buyer.email) &&
    (deliveryMode === "print" ||
      (deliveryMode === "immediate" && /^[^@]+@[^@]+\.[^@]+$/.test(recipient.email || "")) ||
      (deliveryMode === "scheduled" && /^[^@]+@[^@]+\.[^@]+$/.test(recipient.email || "") && deliverAt));

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setErr("");
    try {
      const payload = {
        service_slug: service.slug,
        amount_chf: service.slug === 'montant-libre' ? freeAmount : undefined,
        buyer: {
          prenom: buyer.prenom.trim(),
          nom: buyer.nom.trim(),
          email: buyer.email.trim().toLowerCase(),
          tel: buyer.tel.trim(),
        },
        recipient: {
          prenom: recipient.prenom.trim(),
          nom: recipient.nom.trim(),
          email: deliveryMode === "print" ? null : recipient.email.trim().toLowerCase(),
        },
        message: message.trim(),
        deliver_at: deliveryMode === "scheduled" ? deliverAt : null,
        origin_url: window.location.origin,
      };
      const r = await api.post("/gifts/checkout", payload);
      window.location.href = r.data.url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur lors du paiement. Réessaie.");
      setSubmitting(false);
    }
  };

  return (
    <div className="section-dark min-h-screen pt-28 pb-20 relative" data-testid="offrir-page">
      <Grain />
      <div className="max-w-3xl mx-auto px-6 md:px-12 relative z-10">
        <div className="eyebrow mb-4">Carte Cadeau</div>
        <h1 className="font-serif text-5xl md:text-6xl mb-4" data-testid="offrir-title">
          Offrir une <span className="italic-accent">séance</span>
        </h1>
        <p className="text-text-cream/60 font-light mb-10 max-w-2xl">
          Un cadeau qui compte vraiment. Carte cadeau PDF élégante, code transférable, valable 12 mois.
        </p>

        {cancelled && (
          <div role="status" data-testid="gift-payment-cancelled" className="mb-6 p-3 border border-rose-400/30 bg-rose-500/10 text-rose-200 text-sm rounded">
            Paiement annulé. Tu peux réessayer quand tu veux.
          </div>
        )}

        {/* Service picker (if no slug) */}
        {!service && (
          <div className="mb-10" data-testid="service-picker">
            <h2 className="font-serif text-xl mb-4">Quelle séance veux-tu offrir ?</h2>
            {loading && <p role="status" data-testid="gift-offers-loading" className="mb-4 text-sm">Chargement des offres…</p>}
            {err && <p role="alert" data-testid="gift-offers-load-error" className="mb-4 text-sm">{err}</p>}
            <div className="grid md:grid-cols-2 gap-3">
              {services.map(s => (
                <button
                  key={s.slug}
                  onClick={() => setService(s)}
                  className="text-left flex flex-col items-start justify-start px-4 py-3 rounded border border-white/10 hover:border-accent-deep/50 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
                  data-testid={`offrir-pick-${s.slug}`}
                >
                  <div className="font-serif text-base text-text-cream" data-testid={`gift-offer-price-${s.slug}`}>{s.name} – {offerPrice(s)}</div>
                  <span className="block mt-4 text-text-cream/85"><OfferDetails offer={s} id={`gift-${s.slug}`}/></span>
                </button>
              ))}
              <button data-testid="gift-free-amount-select" onClick={()=>setService({slug:'montant-libre',name:'Bon cadeau — montant libre',duration_min:0,price_chf:freeAmount})} className="text-left px-4 py-3 border border-white/20 hover:border-accent-deep">Montant libre<span className="block mt-2 text-sm" data-testid="gift-minimum-label">Minimum CHF {Math.max(100, settings.gift_minimum_chf || 100)}</span></button>
            </div>
          </div>
        )}

        {service && (
          <>
            {service.slug === 'montant-libre' && <label className="block mb-6" htmlFor="gift-free-amount">Montant du bon (CHF) — minimum CHF {Math.max(100, settings.gift_minimum_chf || 100)}<input id="gift-free-amount" data-testid="gift-free-amount" type="number" min={Math.max(100, settings.gift_minimum_chf || 100)} max={10000} step={1} className={INPUT_CLS} value={freeAmount} onChange={e=>{const value=Number(e.target.value);setFreeAmount(value);setService(s=>({...s,price_chf:value}));}}/></label>}
            {/* Selected service card */}
            <div
              className="mb-8 p-5 border border-accent-deep/30 bg-accent-deep/5 rounded flex items-center gap-4"
              data-testid="offrir-selected"
            >
              <Gift size={24} className="text-accent-light shrink-0" />
              <div className="flex-1">
                <div className="font-serif text-lg">{service.name}</div>
                <div className="text-xs text-text-cream/50 font-light inline-flex items-center gap-2 mt-0.5">
                  {service.duration_min > 0 && <><Clock size={11} aria-hidden="true"/> {durationLabel(service)} · </>}CHF {service.price_chf}
                </div>
                {service.duration_min > 0 && <div className="mt-4"><OfferDetails offer={service} id={`gift-selected-${service.slug}`}/></div>}
              </div>
              {!serviceSlug && (
                <button
                  data-testid="gift-change-offer"
                  onClick={() => setService(null)}
                  className="text-xs text-text-cream/50 hover:text-accent-light"
                >
                  Changer
                </button>
              )}
            </div>

            {/* Buyer */}
            <div className="mb-6">
              <h3 className="eyebrow text-accent-deep mb-3">Toi · acheteur</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <input aria-label="Prénom de l’acheteur" autoComplete="given-name" className={INPUT_CLS} placeholder="Prénom *" value={buyer.prenom}
                  onChange={e => setBuyer({ ...buyer, prenom: e.target.value })} data-testid="buyer-prenom" />
                <input aria-label="Nom de l’acheteur" autoComplete="family-name" className={INPUT_CLS} placeholder="Nom" value={buyer.nom}
                  onChange={e => setBuyer({ ...buyer, nom: e.target.value })} data-testid="buyer-nom" />
                <input aria-label="Email de l’acheteur" autoComplete="email" className={INPUT_CLS} type="email" placeholder="Email *" value={buyer.email}
                  onChange={e => setBuyer({ ...buyer, email: e.target.value })} data-testid="buyer-email" />
                <input aria-label="Téléphone de l’acheteur" autoComplete="tel" className={INPUT_CLS} type="tel" placeholder="Téléphone" value={buyer.tel}
                  onChange={e => setBuyer({ ...buyer, tel: e.target.value })} data-testid="buyer-tel" />
              </div>
            </div>

            {/* Recipient */}
            <div className="mb-6">
              <h3 className="eyebrow text-accent-deep mb-3">Destinataire · à qui l'offres-tu ?</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <input aria-label="Prénom du destinataire" className={INPUT_CLS} placeholder="Prénom" value={recipient.prenom}
                  onChange={e => setRecipient({ ...recipient, prenom: e.target.value })} data-testid="recipient-prenom" />
                <input aria-label="Nom du destinataire" className={INPUT_CLS} placeholder="Nom" value={recipient.nom}
                  onChange={e => setRecipient({ ...recipient, nom: e.target.value })} data-testid="recipient-nom" />
              </div>
              <p className="text-xs text-text-cream/40 font-light mt-2 italic">
                Le code est transférable — si cette personne ne peut pas, elle peut l'offrir à qq d'autre.
              </p>
            </div>

            {/* Delivery mode */}
            <div className="mb-6">
              <h3 className="eyebrow text-accent-deep mb-3">Livraison</h3>
              <div className="grid md:grid-cols-3 gap-2">
                {[
                  { key: "immediate", label: "Email immédiat", icon: Mail },
                  { key: "scheduled", label: "Email planifié", icon: CalIcon },
                  { key: "print", label: "À imprimer", icon: Printer },
                ].map(m => {
                  const Icon = m.icon;
                  const sel = deliveryMode === m.key;
                  return (
                    <button
                      key={m.key}
                      onClick={() => setDeliveryMode(m.key)}
                      className={`py-3 px-4 rounded border text-sm inline-flex items-center justify-center gap-2 transition ${
                        sel
                          ? "border-accent-deep bg-accent-deep/15 text-accent-light"
                          : "border-white/10 text-text-cream/70 hover:border-accent-deep/40"
                      }`}
                      data-testid={`delivery-${m.key}`}
                    >
                      <Icon size={14} /> {m.label}
                    </button>
                  );
                })}
              </div>
              {deliveryMode !== "print" && (
                <input
                  className={`${INPUT_CLS} mt-3`}
                  type="email"
                  placeholder="Email du destinataire *"
                  aria-label="Email du destinataire"
                  value={recipient.email}
                  onChange={e => setRecipient({ ...recipient, email: e.target.value })}
                  data-testid="recipient-email"
                />
              )}
              {deliveryMode === "scheduled" && (
                <input
                  className={`${INPUT_CLS} mt-2`}
                  type="date"
                  value={deliverAt}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={e => setDeliverAt(e.target.value)}
                  data-testid="deliver-at"
                  aria-label="Date d’envoi du bon cadeau"
                />
              )}
              {deliveryMode === "print" && (
                <p className="text-xs text-text-cream/50 font-light mt-2">
                  Le PDF te sera envoyé par email — tu pourras l'imprimer et le remettre en main propre.
                </p>
              )}
            </div>

            {/* Personal message */}
            <div className="mb-8">
              <h3 className="eyebrow text-accent-deep mb-3 inline-flex items-center gap-2">
                <MessageSquare size={12} /> Message personnel (optionnel)
              </h3>
              <textarea
                className={`${INPUT_CLS} resize-none`}
                rows={3}
                placeholder="Un mot pour accompagner le cadeau…"
                value={message}
                onChange={e => setMessage(e.target.value)}
                maxLength={240}
                data-testid="gift-message"
                aria-label="Message personnel du bon cadeau"
              />
              <div className="text-right text-[0.65rem] text-text-cream/30 mt-1">{message.length}/240</div>
            </div>

            {err && (
              <div className="mb-5 p-3 border border-rose-400/30 bg-rose-500/10 text-rose-200 text-sm rounded" data-testid="offrir-error">
                {err}
              </div>
            )}

            <div className="flex items-center justify-between gap-4 pt-6 border-t border-white/10">
              <div>
                <div className="eyebrow text-accent-deep">Total</div>
                <div className="font-serif text-3xl text-accent-light" data-testid="gift-total">CHF {service.price_chf}.00</div>
                <div className="text-xs text-text-cream/40 font-light">Paiement sécurisé Stripe · Valable 12 mois</div>
              </div>
              <button
                onClick={submit}
                disabled={!canSubmit || submitting}
                className="btn-primary disabled:opacity-50"
                data-testid="offrir-submit"
              >
                {submitting ? <><Loader2 className="animate-spin mr-2 inline" size={14} /> Redirection…</> : <>Payer la carte cadeau <ArrowRight size={14} className="ml-2 inline" /></>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
